/**
 * How the ground reflects: the globe and the leaves are three's
 * `MeshStandardMaterial` at these two numbers, and the walkaround's hand-lit
 * shader adds the same specular lobe through `GROUND_SPECULAR_GLSL`, so the
 * SURFACE handover does not change how bright the same ground is lit.
 */

export const GROUND_ROUGHNESS = 0.92;
export const GROUND_METALNESS = 0.02;

/**
 * three's `BRDF_GGX` (`lights_physical_pars_fragment`, 0.170) at the constants
 * above, for a shader three does not light: F0 0.04 mixed toward the albedo by
 * the metalness, F90 1. Multiplied by the irradiance, `N.L` times the light,
 * it is the `directSpecular` a leaf adds over the same ground; on Luna's dark
 * regolith that is about a third of what the leaf reflects.
 */
export const GROUND_SPECULAR_GLSL = /* glsl */ `
vec3 groundSpecular(vec3 N, vec3 L, vec3 V, vec3 albedo) {
  const float alpha = ${(GROUND_ROUGHNESS * GROUND_ROUGHNESS).toFixed(6)};
  const float a2 = alpha * alpha;
  vec3 H = normalize(L + V);
  float nl = clamp(dot(N, L), 0.0, 1.0);
  float nv = clamp(dot(N, V), 0.0, 1.0);
  float nh = clamp(dot(N, H), 0.0, 1.0);
  float vh = clamp(dot(V, H), 0.0, 1.0);
  vec3 f0 = mix(vec3(0.04), albedo, ${GROUND_METALNESS.toFixed(6)});
  float fr = exp2((-5.55473 * vh - 6.98316) * vh);
  vec3 F = f0 * (1.0 - fr) + fr;
  float gv = nl * sqrt(a2 + (1.0 - a2) * nv * nv);
  float gl = nv * sqrt(a2 + (1.0 - a2) * nl * nl);
  float vis = 0.5 / max(gv + gl, 1e-6);
  float d = nh * nh * (a2 - 1.0) + 1.0;
  return F * (vis * a2 / (3.141592653589793 * d * d));
}
`;
